import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useCreateProposal } from '@/hooks/useProposal';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { PROPOSAL_PERMISSIONS, hasPermission } from '@/lib/proposalPermissions';
import {
  useIntakeAssessmentApprovedSummary,
  INTAKE_ASSESSMENT_KIND_LABELS,
} from '@/hooks/useIntakeAssessment';
import {
  INTAKE_ASSESSMENT_PERMISSIONS,
  hasPermission as hasIntakePermission,
} from '@/lib/intakeAssessmentPermissions';
import {
  AssociationLookupSelector,
  type SelectedAssociation,
} from '@/components/business/AssociationLookupSelector';

export default function ProposalCreatePage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { createProposal, loading, error } = useCreateProposal();
  const {
    summary,
    loading: summaryLoading,
    error: summaryError,
    fetchSummary,
    setSummary,
  } = useIntakeAssessmentApprovedSummary();

  const [title, setTitle] = useState('');
  const [customer, setCustomer] = useState<SelectedAssociation | null>(null);
  const [orderId, setOrderId] = useState('');
  const [formError, setFormError] = useState('');

  // Preserve the existing "load approved intake summary" effect, which keys on
  // the selected customer ID. When the user clears the selector, drop the
  // summary so stale data from a prior pick is not shown alongside the new
  // empty state.
  const customerId = customer?.id ?? '';

  const canCreate = hasPermission(permissions, PROPOSAL_PERMISSIONS.PROPOSAL_CREATE);
  const canReadIntake = hasIntakePermission(
    permissions,
    INTAKE_ASSESSMENT_PERMISSIONS.READ,
  );

  // Throttle approved-summary lookups: only fire when a customerId looks like
  // a UUID/CUID-shaped identifier. The server still enforces ownership.
  useEffect(() => {
    if (!canReadIntake) return;
    const trimmed = customerId.trim();
    if (trimmed.length < 8) {
      setSummary(null);
      return;
    }
    const handle = setTimeout(() => {
      void fetchSummary(trimmed);
    }, 350);
    return () => clearTimeout(handle);
  }, [customerId, canReadIntake, fetchSummary, setSummary]);

  if (!canCreate) {
    return <AccessDenied resource="新建方案" />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!title.trim()) {
      setFormError('方案标题不能为空');
      return;
    }
    if (!customer?.id) {
      setFormError('请选择客户');
      return;
    }

    const result = await createProposal({
      title: title.trim(),
      customerId: customer.id,
      ...(orderId.trim() ? { orderId: orderId.trim() } : {}),
    });

    if (result) {
      navigate(`/proposals/${result.id}`);
    }
  }

  const intakeStatusKey = summary
    ? summary.isComplete
      ? 'complete'
      : 'partial'
    : 'none';

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/proposals')}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← 返回列表
        </button>
        <h1 className="text-xl font-semibold">新建方案</h1>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            方案标题 <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="请输入方案标题"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            客户 <span className="text-destructive">*</span>
          </label>
          <AssociationLookupSelector
            kind="customer"
            value={customer}
            onChange={setCustomer}
            placeholder="按姓名搜索客户…"
            required
          />
          {canReadIntake && customerId.trim().length >= 8 && (
            <div
              className={`mt-3 rounded-md border p-3 text-sm ${
                intakeStatusKey === 'complete'
                  ? 'border-green-200 bg-green-50 text-green-900'
                  : intakeStatusKey === 'partial'
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-zinc-200 bg-zinc-50 text-muted-foreground'
              }`}
            >
              {summaryLoading ? (
                <span>正在加载接诊评估摘要…</span>
              ) : summaryError ? (
                <span>未能加载接诊评估摘要：{summaryError.message}</span>
              ) : summary ? (
                <div className="space-y-2">
                  <div className="font-medium">
                    {summary.isComplete
                      ? '该客户已批准三类接诊评估，可作为方案上下文。'
                      : '该客户接诊评估尚不完整，AI 草稿仅可引用已批准摘要。'}
                  </div>
                  <ul className="space-y-1 text-xs">
                    {(['goldenAnalysis', 'dealIntention', 'healthAssessment'] as const).map(
                      (key) => {
                        const entry = summary[key];
                        const labelKey =
                          key === 'goldenAnalysis'
                            ? 'golden_analysis'
                            : key === 'dealIntention'
                              ? 'deal_intention'
                              : 'health_assessment';
                        return (
                          <li key={key}>
                            <span className="font-medium">
                              {INTAKE_ASSESSMENT_KIND_LABELS[labelKey]}：
                            </span>
                            {entry ? (
                              <>
                                <span className="ml-1">{entry.approvedSummary}</span>
                                <span className="ml-1 text-muted-foreground">
                                  （{new Date(entry.approvedAt).toLocaleDateString('zh-CN')}）
                                </span>
                              </>
                            ) : (
                              <span className="ml-1 text-muted-foreground">未批准</span>
                            )}
                          </li>
                        );
                      },
                    )}
                  </ul>
                </div>
              ) : (
                <span>该客户暂无已批准的接诊评估记录。</span>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">关联订单 ID（可选）</label>
          <input
            type="text"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="请输入订单 ID（可选）"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {(formError || error) && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {formError || error?.message}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? '创建中...' : '创建方案'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/proposals')}
            className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            取消
          </button>
        </div>
      </form>
    </div>
  );
}
